import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slide,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"; // For Best Seller icon
import isMobile from "./isMobile";
import { checkProductInStorage, handleAddToList } from "./api/productUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProductDetails = ({
  open,
  handleClose,
  productDetails,
  setProducts,
  products,
}) => {
  const mobileView = isMobile();
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const charLimit = mobileView ? 40 : 60;

  const {
    name,
    clean_image_url,
    image_url,
    description,
    current_price,
    merchant_logo,
    _L1,
    _L2,
    post_price_text,
    valid_from,
    valid_to,
    sale_story,
    global_id,
    buy_online,
    buy_in_store,
    media,
    url,
    brand,
    ship_to_home,
    ship_to_store,
  } = productDetails;
  const imageUrl = clean_image_url || image_url;
  const largeImage = media && media.length > 0 && media[0].url;
  const merchant = productDetails.merchant || productDetails.merchant_name;
  const validFromDate = new Date(valid_from).toDateString();
  const validTillDate = new Date(valid_to).toDateString();

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleAdd = () => {
    handleAddToList(productDetails, setProducts);
    setMessage("Product Added");
    setTimeout(() => setMessage(""), 2000);
  };

  const handleRemove = () => {
    const storedProducts = JSON.parse(localStorage.getItem("products")) || [];
    if (storedProducts.length > 0) {
      const updatedProducts = storedProducts.filter(
        (product) => product.id !== productDetails.id
      );
      localStorage.setItem("products", JSON.stringify(updatedProducts));
      setMessage("Product Removed");
      setTimeout(() => {
        setProducts(updatedProducts);
        setMessage("");
        handleClose(); // Close the dialog after the message is displayed
      }, 2000);
    }
  };

  // Truncate the item name if necessary
  const truncatedDesc =
    description && description.length > charLimit
      ? description.substring(0, charLimit) + "..."
      : description;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        style: {
          backgroundColor: "#fff",
          borderRadius: mobileView ? 25 : 16,
          margin: mobileView ? 0 : 40, // Leave space above (40px)
          maxWidth: mobileView ? "100%" : "600px",
          width: "100%",
          height: mobileView ? "calc(100% - 0px)" : "calc(100% - 80px)", // Adjust the height to fill the viewport minus the margin
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Close Icon */}
      <Box
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px",
        }}
      >
        <IconButton onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        {/* Add space before the image container */}
        <Box style={{ height: 20 }} />

        {/* Image Container */}
        <Box
          style={{
            position: "relative",
            width: "100%",
            height: mobileView ? "300px" : "400px",
            overflow: "hidden",
            padding: "0 20px", // Add horizontal padding
          }}
        >
          {/* Best Seller Badge */}
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 20,
              backgroundColor: "#fff",
              color: "#1E90FF", // Dodger Blue color
              padding: "4px 8px",
              borderRadius: 4,
              border: "1px solid #1E90FF", // Blue border
              display: "flex",
              alignItems: "center",
              zIndex: 5,
              // marginTop: -10,
            }}
          >
            <LocalOfferOutlinedIcon
              style={{ marginRight: 4, fontSize: "1rem" }}
            />
            <Typography variant="caption" style={{ fontWeight: "bold" }}>
              {validTillDate !== "Invalid Date" &&
                `Valid Till ${validTillDate} | Offline Available`}
              {buy_online && buy_in_store && `Online | Offline Available`}
              {buy_online && !buy_in_store && `Online Deal`}
              {buy_in_store && !buy_online && `Offline Deal`}
            </Typography>
          </Box>

          {/* Product Image */}
          <img
            src={largeImage || imageUrl}
            alt={`${name} Image`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Product Details */}
        <Box
          style={{
            padding: "20px",
            backgroundColor: "#fff",
            marginBottom: "20px",
          }}
        >
          <Typography
            variant="h6"
            style={{ fontWeight: "bold", marginBottom: 8 }}
          >
            {name}
          </Typography>
          {(sale_story || ship_to_home || ship_to_store) && (
            <Box
              style={{
                top: 0,
                left: 20,
                backgroundColor: "#fff",
                color: "#1E90FF", // Dodger Blue color
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid #1E90FF", // Blue border
                display: "inline-flex", // Adjust width to content
                alignItems: "center",
                zIndex: 5,
                marginBottom: "10px",
              }}
            >
              <LocalOfferOutlinedIcon
                style={{ marginRight: 4, fontSize: "1rem" }}
              />
              <Typography variant="caption" style={{ fontWeight: "bold" }}>
                {sale_story && sale_story}
                {ship_to_home && `Home Delivery Available`}
                {ship_to_store && `Pick Up At Store Available`}
              </Typography>
            </Box>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "5px",
              flexShrink: 0,
            }}
          >
            {merchant_logo && (
              <img
                src={merchant_logo}
                alt={`${merchant} Logo`}
                style={{
                  maxWidth: "40px",
                  maxHeight: "40px",
                  objectFit: "contain",
                  marginRight: "5px",
                }}
              />
            )}
            <Typography
              variant="h6"
              color="text.secondary"
              style={{
                textAlign: "left",
                marginBottom: "5px",
              }}
            >
              {merchant}
            </Typography>
          </div>
          {description && (
            <Box
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "30px",
                marginTop: "10px",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                Product details
              </Typography>
              <Typography variant="p" style={{ fontWeight: "bold" }}>
                {brand}
              </Typography>
              <Typography variant="body1" style={{ marginBottom: "16px" }}>
                {!expanded ? (
                  <>
                    {truncatedDesc}
                    {description.length > charLimit && (
                      <span
                        onClick={toggleExpand}
                        style={{ color: "blue", cursor: "pointer" }}
                      >
                        {" "}
                        Read more
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {description}
                    {description.length > charLimit && (
                      <span
                        onClick={toggleExpand}
                        style={{ color: "blue", cursor: "pointer" }}
                      >
                        {" "}
                        Read less
                      </span>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Bottom Bar with Price and Button */}
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          padding: "16px 20px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" style={{ fontWeight: "bold" }}>
          ${current_price}
          {post_price_text && ` / ${post_price_text}`}
        </Typography>
        <Box style={{ display: "flex", gap: "10px" }}>
          {message && (
            <>
              <Typography
                variant="body2"
                color="text.primary"
                style={{ marginRight: "3px", marginTop: "8px" }}
              >
                {message}
              </Typography>
              <CheckIcon color="success" sx={{ marginTop: '5px' }} />
            </>
          )}
          {!checkProductInStorage(productDetails, products) && !message ? (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleAdd();
              }}
            >
              Add To List
            </Button>
          ) : (
            !message && (
              <Button
                variant="contained"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                Remove
              </Button>
            )
          )}
          {url && (
            <Button
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = url;
              }}
              color="primary"
            >
              Buy Now
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProductDetails;
